import { createContext, useContext, useState, useMemo, useCallback, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { isWorkRole } from '../lib/membershipScope'
import type {
  MembershipContext,
  UserDetailResponse,
  UserContextResponse,
  UnitContext,
  MembershipResponse,
  MembershipRole,
  MembershipEntityType,
} from '../types'

interface PrimaryContext {
  productTeamId: string | null
  productTeamName: string | null
  valueStreamId: string | null
  valueStreamName: string | null
  divisionId: string | null
  divisionName: string | null
  organizationId: string | null
  organizationName: string | null
  role: MembershipRole | null
  entityType: MembershipEntityType | null
  /**
   * The whole chain, root first, the user's own unit last.
   *
   * The four named slots above are this path, read through the four level codes
   * the product shipped with, and they are kept because most of the app still
   * asks for them by name. They go null for a tenant whose ladder has no such
   * level -- which is the correct answer, not a missing one. New code should
   * read `path` and `unit` instead: those are right at any depth.
   */
  path: UnitContext[]
  /** The unit the membership is actually on: the last node of the path. */
  unit: UnitContext | null
}

interface UserContextType {
  userDetail: UserDetailResponse | null
  /** Primary WORK context (derived from work-track memberships only; null for pure admins) */
  primaryContext: PrimaryContext | null
  hasOrgAdminAccess: boolean
  /** True if user has org_admin Keycloak role (system configurators) */
  isOrgAdmin: boolean
  isLoading: boolean
  error: Error | null
  refetch: () => void
  /** All active memberships for the current user */
  activeMemberships: MembershipResponse[]
  /** Active work-track memberships (VIEWER/CONTRIBUTOR/MANAGER) — these carry content access */
  workMemberships: MembershipResponse[]
  /** True when the user has admin access but no work-track membership: administration is their only surface */
  isPureAdmin: boolean
  /** Organization the user administers (from ADMIN/OWNER membership, falling back to the work chain) */
  adminOrganizationId: string | null
  /** Switch to a different membership context by ID */
  setActiveMembership: (membershipId: string) => void
  /** Reset to auto-determined primary membership */
  clearActiveMembership: () => void
}

const UserContext = createContext<UserContextType | undefined>(undefined)

const ROLE_PRIORITY: Record<MembershipRole, number> = {
  OWNER: 5,
  ADMIN: 4,
  MANAGER: 3,
  CONTRIBUTOR: 2,
  VIEWER: 1,
}

const ENTITY_PRIORITY: Record<MembershipEntityType, number> = {
  PRODUCT_TEAM: 4,
  VALUE_STREAM: 3,
  DIVISION: 2,
  ORGANIZATION: 1,
  // A unit on a tenant's own level sits between a value stream and a team as a
  // guess, and it is only a guess: the type carries no depth. It matters only
  // for picking which context to open on when someone holds several, and the
  // path from /users/me/context has the real depth once one is chosen.
  UNIT: 3,
}

function determinePrimaryMembership(
  memberships: MembershipResponse[]
): MembershipResponse | null {
  // Only work-track memberships define the user's work context; ADMIN/OWNER
  // memberships belong to the admin console, not the work surfaces.
  const active = memberships.filter((m) => m.status === 'ACTIVE' && isWorkRole(m.role))
  if (active.length === 0) return null

  // Sort by entity priority (PRODUCT_TEAM first), then role priority
  return active.sort((a, b) => {
    const entityDiff = ENTITY_PRIORITY[b.entityType] - ENTITY_PRIORITY[a.entityType]
    if (entityDiff !== 0) return entityDiff
    return ROLE_PRIORITY[b.role] - ROLE_PRIORITY[a.role]
  })[0]
}

function computeHasOrgAdminAccess(memberships: MembershipResponse[]): boolean {
  return memberships.some(
    (m) =>
      m.status === 'ACTIVE' &&
      m.entityType === 'ORGANIZATION' &&
      (m.role === 'ADMIN' || m.role === 'OWNER')
  )
}

const MEMBERSHIP_STORAGE_KEY = 'okr-saas-active-membership'

// Selection keys for token grants are UI state, never membership IDs for write APIs.
const contextMembershipKey = (m: MembershipContext) =>
  m.membershipId ?? `group:${m.entityType}:${m.entityId}:${m.role}`

export function UserContextProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()

  // Override membership selection (persisted in localStorage)
  const [overrideMembershipId, setOverrideMembershipId] = useState<string | null>(
    () => localStorage.getItem(MEMBERSHIP_STORAGE_KEY)
  )

  const setActiveMembership = useCallback((membershipId: string) => {
    localStorage.setItem(MEMBERSHIP_STORAGE_KEY, membershipId)
    setOverrideMembershipId(membershipId)
  }, [])

  const clearActiveMembership = useCallback(() => {
    localStorage.removeItem(MEMBERSHIP_STORAGE_KEY)
    setOverrideMembershipId(null)
  }, [])

  // 1. Fetch current user detail (includes memberships)
  const {
    data: userDetail,
    isLoading: isUserLoading,
    error: userError,
    refetch,
  } = useQuery<UserDetailResponse>({
    queryKey: ['currentUserDetail'],
    queryFn: () => api.users.me().then((res) => res.data),
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 10,
  })

  // 2. Where those memberships sit, in one request.
  //
  // This used to be five queries in sequence -- team, then its value stream,
  // then that division, then the organization -- each enabled only once the
  // previous had returned an id. Four levels were hardcoded in the chain, so a
  // tenant with three or six could not have been drawn correctly.
  const { data: contextData, isLoading: isContextLoading } = useQuery<UserContextResponse>({
    queryKey: ['currentUserContext'],
    queryFn: () => api.users.context().then((res) => res.data),
    // /users/me is the request that links a first sign-in to the invited user
    // row. Sent alongside it, this one looked the user up before that link
    // existed and failed with a 404.
    enabled: isAuthenticated && !!userDetail,
    staleTime: 1000 * 60 * 10,
  })

  // Context memberships are the backend's effective view, including current-token
  // group grants on-prem. The browser does not reconstruct those mappings.
  const activeMemberships = useMemo<MembershipResponse[]>(
    () => contextData?.memberships.map((m) => ({
      id: contextMembershipKey(m),
      entityType: m.entityType,
      entityId: m.entityId,
      role: m.role,
      source: m.source,
      status: 'ACTIVE',
    })) ?? userDetail?.memberships?.filter((m) => m.status === 'ACTIVE') ?? [],
    // Not the whole `userDetail`: /users/me answers with a fresh `updatedAt` on every
    // call, and the token refresh refetches it each minute. Keyed on it, every
    // `primaryContext` consumer got a new object each minute; an open objective form
    // re-seeded its defaults and lost what the user had typed.
    [contextData, userDetail?.memberships]
  )
  const workMemberships = useMemo(
    () => activeMemberships.filter((m) => isWorkRole(m.role)),
    [activeMemberships]
  )
  const primaryMembership = useMemo(() => {
    if (!userDetail) return null
    if (overrideMembershipId) {
      const override = workMemberships.find((m) => m.id === overrideMembershipId)
      if (override) return override
      localStorage.removeItem(MEMBERSHIP_STORAGE_KEY)
    }
    return determinePrimaryMembership(activeMemberships)
  }, [userDetail, overrideMembershipId, workMemberships, activeMemberships])

  const isOrgAdmin = contextData?.roles?.some((role) => role === 'org_admin' || role === 'super_admin') === true

  const pathsByMembership = useMemo(() => {
    const map = new Map<string, UnitContext[]>()
    contextData?.memberships?.forEach((m) => map.set(contextMembershipKey(m), m.path))
    return map
  }, [contextData])

  const primaryContext = useMemo<PrimaryContext | null>(() => {
    if (!primaryMembership) return null
    const path = pathsByMembership.get(primaryMembership.id) ?? []
    const at = (levelCode: MembershipEntityType) => path.find((node) => node.levelCode === levelCode) ?? null
    const team = at('PRODUCT_TEAM')
    const valueStream = at('VALUE_STREAM')
    const division = at('DIVISION')
    // The root is the organization whatever it is called, so it is taken by
    // position rather than by code -- a tenant may rename it, and every path
    // has one.
    const organization = at('ORGANIZATION') ?? path[0] ?? null

    return {
      productTeamId: team?.id ?? null,
      productTeamName: team?.name ?? null,
      valueStreamId: valueStream?.id ?? null,
      valueStreamName: valueStream?.name ?? null,
      divisionId: division?.id ?? null,
      divisionName: division?.name ?? null,
      organizationId: organization?.id ?? null,
      organizationName: organization?.name ?? null,
      role: primaryMembership.role,
      entityType: primaryMembership.entityType,
      path,
      unit: path.length > 0 ? path[path.length - 1] : null,
    }
  }, [primaryMembership, pathsByMembership])

  // System roles and effective memberships come from the backend context.
  const hasOrgAdminAccess = useMemo(
    () => isOrgAdmin || (userDetail ? computeHasOrgAdminAccess(activeMemberships) : false),
    [isOrgAdmin, userDetail, activeMemberships]
  )

  const isPureAdmin = hasOrgAdminAccess && workMemberships.length === 0

  // Organization the user administers: prefer an explicit ADMIN/OWNER org
  // membership, fall back to the resolved work chain (dual-hat users).
  const adminOrganizationId = useMemo(() => {
    if (!hasOrgAdminAccess) return null
    const adminMembership = activeMemberships.find(
      (m) => m.entityType === 'ORGANIZATION' && (m.role === 'ADMIN' || m.role === 'OWNER')
    )
    return adminMembership?.entityId ?? primaryContext?.organizationId ?? null
  }, [hasOrgAdminAccess, activeMemberships, primaryContext?.organizationId])

  const isLoading = isUserLoading || isContextLoading

  const value = useMemo<UserContextType>(
    () => ({
      userDetail: userDetail ?? null,
      primaryContext,
      hasOrgAdminAccess,
      isOrgAdmin,
      isLoading,
      error: userError as Error | null,
      refetch,
      activeMemberships,
      workMemberships,
      isPureAdmin,
      adminOrganizationId,
      setActiveMembership,
      clearActiveMembership,
    }),
    [userDetail, primaryContext, hasOrgAdminAccess, isOrgAdmin, isLoading, userError, refetch, activeMemberships, workMemberships, isPureAdmin, adminOrganizationId, setActiveMembership, clearActiveMembership]
  )

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export function useUserContext() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserContextProvider')
  }
  return context
}
